import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import "./SignInPage.css";
import logo from "../../assets/logo.png";
import { useAuth } from "../../context/AuthContext";
import {
  validateRegistrationForm,
  validateEmailDetailed,
} from "../../utils/validation";

// Custom styles for react-select
const customSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: '44px',
    height: '44px',
    padding: '0 12px',
    borderColor: state.selectProps.hasError ? '#dc3545' : state.isFocused ? '#03c0a7' : '#e0e0e0',
    backgroundColor: state.selectProps.hasError ? '#fff5f5' : '#fff',
    boxShadow: state.isFocused 
      ? state.selectProps.hasError 
        ? '0 0 0 3px rgba(220, 53, 69, 0.1)' 
        : '0 0 0 3px rgba(3, 192, 167, 0.1)' 
      : 'none',
    '&:hover': {
      borderColor: state.selectProps.hasError ? '#dc3545' : '#03c0a7',
    },
    fontSize: '14px',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  }),
  placeholder: (base) => ({
    ...base,
    color: '#999',
    fontSize: '14px',
    margin: 0,
    padding: 0,
  }),
  valueContainer: (base) => ({
    ...base,
    padding: '9px 0',
  }),
  singleValue: (base) => ({
    ...base,
    color: '#333',
    fontSize: '14px',
    lineHeight: '22px',
    margin: 0,
    padding: 0,
  }),
  menu: (base) => ({
    ...base,
    zIndex: 9999,
    borderRadius: '8px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
  }),
  menuList: (base) => ({
    ...base,
    maxHeight: '200px',
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? '#03c0a7' : state.isFocused ? '#e6f9f6' : '#fff',
    color: state.isSelected ? '#fff' : '#333',
    fontSize: '14px',
    padding: '10px 12px',
    cursor: 'pointer',
    '&:active': {
      backgroundColor: '#03c0a7',
    },
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  dropdownIndicator: (base) => ({
    ...base,
    color: '#999',
    '&:hover': {
      color: '#03c0a7',
    },
  }),
  clearIndicator: () => ({
    display: 'none',
  }),
};

// Generate options
const generateDayOptions = () => 
  Array.from({ length: 31 }, (_, i) => ({
    value: String(i + 1).padStart(2, "0"),
    label: String(i + 1),
  }));

const monthOptions = [
  { value: "01", label: "January" },
  { value: "02", label: "February" },
  { value: "03", label: "March" },
  { value: "04", label: "April" },
  { value: "05", label: "May" },
  { value: "06", label: "June" },
  { value: "07", label: "July" },
  { value: "08", label: "August" },
  { value: "09", label: "September" },
  { value: "10", label: "October" },
  { value: "11", label: "November" },
  { value: "12", label: "December" },
];

const generateYearOptions = () => 
  Array.from({ length: 82 }, (_, i) => {
    const year = new Date().getFullYear() - 18 - i;
    return { value: String(year), label: String(year) };
  });

const genderOptions = [
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
  { value: "other", label: "Other" },
];

const placeOptions = [
  { value: "ahmedabad", label: "Ahmedabad" },
  { value: "bangalore", label: "Bangalore" },
  { value: "bhopal", label: "Bhopal" },
  { value: "chennai", label: "Chennai" },
  { value: "delhi", label: "Delhi" },
  { value: "hyderabad", label: "Hyderabad" },
  { value: "indore", label: "Indore" },
  { value: "jaipur", label: "Jaipur" },
  { value: "kanpur", label: "Kanpur" },
  { value: "kolkata", label: "Kolkata" },
  { value: "lucknow", label: "Lucknow" },
  { value: "mumbai", label: "Mumbai" },
  { value: "nagpur", label: "Nagpur" },
  { value: "patna", label: "Patna" },
  { value: "pune", label: "Pune" },
  { value: "surat", label: "Surat" },
  { value: "other", label: "Other" },
];

const professionOptions = [
  { value: "business", label: "Business Owner" },
  { value: "consultant", label: "Consultant" },
  { value: "employee", label: "Employee" },
  { value: "entrepreneur", label: "Entrepreneur" },
  { value: "freelancer", label: "Freelancer" },
  { value: "professional", label: "Professional" },
  { value: "retired", label: "Retired" },
  { value: "student", label: "Student" },
  { value: "other", label: "Other" },
];

const dayOptions = generateDayOptions();
const yearOptions = generateYearOptions();

const SignInPage = () => {
  const navigate = useNavigate();
  const { register, isAuthenticated } = useAuth();
  
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    mobile: "",
    day: "",
    month: "",
    year: "",
    gender: "",
    place: "",
    profession: "",
    membershipNo: "",
  });

  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [validationErrors, setValidationErrors] = useState({});

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Handle text input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "mobile") {
      const onlyNumbers = value.replace(/\D/g, "");
      setFormData((prev) => ({ ...prev, [name]: onlyNumbers }));
      if (validationErrors.mobile) {
        setValidationErrors((prev) => ({ ...prev, mobile: "" }));
      }
      return;
    }

    if (name === "email") {
      const lowercaseValue = value.toLowerCase();
      setFormData((prev) => ({ ...prev, [name]: lowercaseValue }));
      if (validationErrors.email) {
        setValidationErrors((prev) => ({ ...prev, email: "" }));
      }
      return;
    }

    if (name === "fullName") {
      const lettersOnly = value.replace(/[^a-zA-Z\s]/g, "");
      setFormData((prev) => ({ ...prev, [name]: lettersOnly }));
      if (validationErrors.fullName) {
        setValidationErrors((prev) => ({ ...prev, fullName: "" }));
      }
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // Handle select change
  const handleSelectChange = (name, selectedOption) => {
    setFormData((prev) => ({
      ...prev,
      [name]: selectedOption ? selectedOption.value : "",
    }));
    // Clear dateOfBirth error when any date field changes
    if (name === 'day' || name === 'month' || name === 'year') {
      if (validationErrors.dateOfBirth) {
        setValidationErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors.dateOfBirth;
          return newErrors;
        });
      }
    } else if (validationErrors[name]) {
      setValidationErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // Get selected option
  const getSelectedOption = (options, value) => {
    return options.find((opt) => opt.value === value) || null;
  };

  // Check if form is valid (all required fields filled)
  const isFormValid = () => {
    return (
      formData.fullName.trim() &&
      formData.email.trim() &&
      /^\d{10}$/.test(formData.mobile) &&
      formData.day &&
      formData.month &&
      formData.year &&
      formData.gender &&
      formData.place &&
      formData.profession
    );
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setValidationErrors({});

    const validation = validateRegistrationForm(formData);
    if (!validation.isValid) {
      const errors = {};
      validation.errors.forEach((err) => {
        errors[err.field] = err.message;
      });
      setValidationErrors(errors);
      return;
    }

    const emailValidation = validateEmailDetailed(formData.email);
    if (!emailValidation.isValid) {
      setValidationErrors((prev) => ({ ...prev, email: emailValidation.error }));
      return;
    }

    if (!/^\d{10}$/.test(formData.mobile)) {
      setValidationErrors((prev) => ({
        ...prev,
        mobile: "Mobile number must be exactly 10 digits",
      }));
      return;
    }

    if (!agreeToTerms) {
      setValidationErrors({ terms: "Please agree to the terms and conditions" });
      return;
    }

    setLoading(true);

    try {
      const dateOfBirth = formData.year && formData.month && formData.day
        ? `${formData.year}-${formData.month}-${formData.day}`
        : undefined;

      const apiData = {
        fullName: formData.fullName,
        email: formData.email,
        mobile: formData.mobile,
        dateOfBirth,
        gender: formData.gender,
        place: formData.place,
        profession: formData.profession,
        membershipNo: formData.membershipNo || undefined,
      };

      const response = await register(apiData);

      if (response.success) {
        navigate('/otp', {
          state: { mobile: formData.mobile, type: 'registration' }
        });
      } else {
        if (response.message && (response.message.includes('already registered') || response.message.includes('already exists'))) {
          navigate(`/login?mobile=${encodeURIComponent(formData.mobile)}&message=${encodeURIComponent('Mobile number already registered. Please login instead.')}`);
        } else {
          setError(response.message || "Registration failed. Please try again.");
        }
      }
    } catch (err) {
      if (err.message && (err.message.includes('already registered') || err.message.includes('already exists'))) {
        navigate(`/login?mobile=${encodeURIComponent(formData.mobile)}&message=${encodeURIComponent('Mobile number already registered. Please login instead.')}`);
      } else {
        setError(err.message || "Registration failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="signin-page">
      <div className="signin-page-content">
        <div className="row g-0 h-100">
          {/* Left Column */}
          <div className="col-lg-5 col-md-6 col-sm-12">
            <div className="signin-left-content">
              <div className="signin-page-left">
                <div className="signin-logo">
                  <img src={logo} alt="ComplyV" />
                </div>
                <div className="signin-highlight">SIMPLIFY YOUR LEGAL JOURNEY</div>
                <div className="signin-description">
                  <span className="color-text">Get expert support</span>{" "}
                  <span className="simple-text">for registrations,</span>{" "}
                  <span className="italic-text">filings, and compliance</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Form */}
          <div className="col-lg-7 col-md-6 col-sm-12">
            <div className="signin-page-right">
              <div className="signin-page-right-content">
                <div className="form-container">
                  <div className="form-header">
                    <h2 className="form-title">Create account</h2>
                    <p className="form-subtitle">Sign up with your mobile number to get started.</p>
                  </div>

                  <div className="form-scroll-container">
                    <form onSubmit={handleSubmit} className="signin-form" noValidate>
                      
                      {/* Row 1: Full Name & Email */}
                      <div className="form-row">
                        <div className="form-group">
                          <label>Full name *</label>
                          <input
                            type="text"
                            name="fullName"
                            value={formData.fullName}
                            onChange={handleInputChange}
                            className={`form-input ${validationErrors.fullName ? "error" : ""}`}
                            placeholder="Enter your full name"
                          />
                          {validationErrors.fullName && <div className="field-error">{validationErrors.fullName}</div>}
                        </div>
                        <div className="form-group">
                          <label>Email ID *</label>
                          <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            className={`form-input ${validationErrors.email ? "error" : ""}`}
                            placeholder="example@domain.com"
                          />
                          {validationErrors.email && <div className="field-error">{validationErrors.email}</div>}
                        </div>
                      </div>

                      {/* Row 2: Mobile */}
                      <div className="form-row">
                        <div className="form-group">
                          <label>Mobile number *</label>
                          <input
                            type="tel"
                            name="mobile"
                            value={formData.mobile}
                            onChange={handleInputChange}
                            maxLength={10}
                            className={`form-input ${validationErrors.mobile ? "error" : ""}`}
                            placeholder="Enter 10-digit mobile"
                          />
                          {validationErrors.mobile && <div className="field-error">{validationErrors.mobile}</div>}
                        </div>
                        <div className="form-group">
                        <label>Date of birth *</label>
                        <div className="dob-row">
                          <div className="dob-field dob-day">
                            <Select
                              value={getSelectedOption(dayOptions, formData.day)}
                              onChange={(opt) => handleSelectChange("day", opt)}
                              options={dayOptions}
                              placeholder="Day"
                              isSearchable
                              styles={customSelectStyles}
                              hasError={!!validationErrors.dateOfBirth}
                            />
                          </div>
                          <div className="dob-field dob-month">
                            <Select
                              value={getSelectedOption(monthOptions, formData.month)}
                              onChange={(opt) => handleSelectChange("month", opt)}
                              options={monthOptions}
                              placeholder="Month"
                              isSearchable
                              styles={customSelectStyles}
                              hasError={!!validationErrors.dateOfBirth}
                            />
                          </div>
                          <div className="dob-field">
                            <Select
                              value={getSelectedOption(yearOptions, formData.year)}
                              onChange={(opt) => handleSelectChange("year", opt)}
                              options={yearOptions}
                              placeholder="Year"
                              isSearchable
                              styles={customSelectStyles}
                              hasError={!!validationErrors.dateOfBirth}
                            />
                          </div>
                        </div>
                        {validationErrors.dateOfBirth && (
                          <div className="field-error">{validationErrors.dateOfBirth}</div>
                        )}
                      </div>
                      </div>

                      {/* Row 3: Date of Birth - Full Width */}
                     

                      {/* Row 4: Gender & Place */}
                      <div className="form-row">
                        <div className="form-group">
                          <label>Gender *</label>
                          <Select
                            value={getSelectedOption(genderOptions, formData.gender)}
                            onChange={(opt) => handleSelectChange("gender", opt)}
                            options={genderOptions}
                            placeholder="Select Gender"
                            isClearable
                            styles={customSelectStyles}
                            hasError={!!validationErrors.gender}
                          />
                          {validationErrors.gender && <div className="field-error">{validationErrors.gender}</div>}
                        </div>
                        <div className="form-group">
                          <label>Place *</label>
                          <Select
                            value={getSelectedOption(placeOptions, formData.place)}
                            onChange={(opt) => handleSelectChange("place", opt)}
                            options={placeOptions}
                            placeholder="Type to search..."
                            isClearable
                            isSearchable
                            styles={customSelectStyles}
                            hasError={!!validationErrors.place}
                          />
                          {validationErrors.place && <div className="field-error">{validationErrors.place}</div>}
                        </div>
                      </div>

                      {/* Row 5: Profession & Membership */}
                      <div className="form-row">
                        <div className="form-group">
                          <label>Profession *</label>
                          <Select
                            value={getSelectedOption(professionOptions, formData.profession)}
                            onChange={(opt) => handleSelectChange("profession", opt)}
                            options={professionOptions}
                            placeholder="Select Profession"
                            isClearable
                            isSearchable
                            styles={customSelectStyles}
                            hasError={!!validationErrors.profession}
                          />
                          {validationErrors.profession && <div className="field-error">{validationErrors.profession}</div>}
                        </div>
                        <div className="form-group">
                          <label>Membership no.</label>
                          <input
                            type="text"
                            name="membershipNo"
                            value={formData.membershipNo}
                            onChange={handleInputChange}
                            className="form-input"
                            placeholder="Optional"
                          />
                        </div>
                      </div>

                      {/* Terms */}
                      <div className="form-checkbox">
                        <input
                          type="checkbox"
                          id="agreeTerms"
                          checked={agreeToTerms}
                          onChange={(e) => {
                            setAgreeToTerms(e.target.checked);
                            if (validationErrors.terms) setValidationErrors((prev) => ({ ...prev, terms: "" }));
                          }}
                          className="checkbox-input"
                        />
                        <label htmlFor="agreeTerms" className="checkbox-label">
                          I agree to all the <a href="#terms" className="link">Terms</a> and <a href="#privacy" className="link">Privacy policy</a>
                        </label>
                      </div>
                      {validationErrors.terms && <div className="field-error">{validationErrors.terms}</div>}

                      {/* Submit */}
                      <button type="submit" className={`create-submit-btn ${loading ? "loading" : ""}`} disabled={loading}>
                        {loading ? "Creating Account..." : "Submit"}
                      </button>

                      {error && <div className="error-message">{error}</div>}

                      <div className="login-link">
                        Already have an account? <button type="button" onClick={() => navigate("/login")} className="login-btn">Login</button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignInPage;
